import { Button, type ButtonProps } from '../primitives/button.js'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Share } from '@tunasong/icons'
import { entitySort } from '@tunasong/models'
import type { FC } from 'react'
import { useCallback, useState } from 'react'
import { EntityLink } from '../entity/entity-link.js'
import { Menu, MenuItem } from '../primitives/index.js'

export interface BacklinksButtonProps extends Omit<ButtonProps, 'variant'> {
  id?: string
  /**
   * `icon`: a count with the backlinks icon, disabled at 0.
   * `text`: "2 backlinks" with no icon, and nothing at all at 0.
   * @default 'icon'
   */
  variant?: 'icon' | 'text'
  /** `text` only: end the label with an arrow, as a line that opens a list (GH #2519 C). @default false */
  arrow?: boolean
}

export const BacklinksButton: FC<BacklinksButtonProps> = ({
  id = '',
  variant = 'icon',
  arrow = false,
  ...restProps
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])
  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])
  const { backlinks = [] } = graphHooks.useEntityBacklinks({ entityId: id })

  const ids = backlinks
    .map(backlink => backlink.source)
    .filter(id => id !== 'ROOT')
    .filter(Boolean)
  const { entities: linkedEntities = [] } = graphHooks.useEntitiesById(ids)
  const entities = entitySort({ entities: linkedEntities })

  const numBacklinks = backlinks.length

  const isText = variant === 'text'
  if (isText && numBacklinks === 0) {
    return null
  }

  const countLabel = numBacklinks > 0 ? `${numBacklinks}` : ''
  const label = isText
    ? `${numBacklinks} ${numBacklinks === 1 ? 'backlink' : 'backlinks'}${arrow ? ' →' : ''}`
    : countLabel

  return (
    <>
      <Button
        {...restProps}
        endIcon={isText ? undefined : <Share />}
        aria-label={`Show ${numBacklinks} ${numBacklinks === 1 ? 'backlink' : 'backlinks'}`}
        aria-controls={open ? 'backlink-button' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        disabled={backlinks.length === 0}
        onClick={handleClick}
      >
        {label}
      </Button>

      <Menu
        id="backlink-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'backlink-button',
        }}
      >
        <MenuItem disabled>Backlinks</MenuItem>
        {entities.map(entity =>
          entity ? (
            <MenuItem onClick={handleClose} key={entity.id}>
              <EntityLink entity={entity} onClick={handleClose} />
            </MenuItem>
          ) : null
        )}
      </Menu>
    </>
  )
}

export default BacklinksButton
